import ChevronDoubleSvg from '@/assets/icons/chevron-left-double.svg';
import ChevronSvg from '@/assets/icons/chevron-left.svg';
import { ButtonProps } from '@/shared/components/atoms/Button';
import { cn } from '@/styles/utils';
import * as React from 'react';

const PaginationRoot = ({
  className,
  ...props
}: React.ComponentProps<'nav'>) => (
  <nav
    role="navigation"
    className={cn('mx-auto flex w-full justify-center', className)}
    {...props}
  />
);
PaginationRoot.displayName = 'Pagination';

const PaginationContent = React.forwardRef<
  HTMLUListElement,
  React.ComponentProps<'ul'>
>(({ className, ...props }, ref) => (
  <ul
    ref={ref}
    className={cn('flex flex-row items-center gap-1', className)}
    {...props}
  />
));
PaginationContent.displayName = 'PaginationContent';

const PaginationItem = React.forwardRef<
  HTMLLIElement,
  React.ComponentProps<'li'>
>(({ className, ...props }, ref) => (
  <li ref={ref} className={cn(className)} {...props} />
));
PaginationItem.displayName = 'PaginationItem';

type PaginationLinkProps = {
  isActive?: boolean;
  isDisabled?: boolean;
} & Pick<ButtonProps, 'size'> &
  React.ComponentProps<'a'>;

const PaginationLink = ({
  className,
  isActive,
  isDisabled,
  ...props
}: PaginationLinkProps) => (
  <a
    aria-current={isActive ? 'page' : undefined}
    aria-disabled={isDisabled}
    className={cn(
      'flex items-center justify-center min-w-6 min-h-6 transition-colors duration-200 w-8 h-8 m-2 rounded-full',
      isActive
        ? 'body-16b text-[#C21E0C] bg-[#FAE6DE] hover:bg-[#FAE6DE] hover:text-[#C21E0C]'
        : 'body-16r text-[#6D717D] bg-transparent hover:text-[#C21E0C]',
      isDisabled &&
      'opacity-50 cursor-not-allowed hover:bg-transparent hover:text-[#DADEE5]',
      className,
    )}
    {...props}
  />
);
PaginationLink.displayName = 'PaginationLink';

const PaginationPreviousPageGroup = ({
  className,
  isDisabled,
  ...props
}: React.ComponentProps<typeof PaginationLink>) => (
  <PaginationLink
    size="default"
    className={cn(className)}
    isDisabled={isDisabled}
    {...props}
  >
    <ChevronSvg
      stroke={isDisabled ? '#DADEE5' : '#6D717D'}
      width={24}
      height={24}
    />
  </PaginationLink>
);
PaginationPreviousPageGroup.displayName = 'PaginationPreviousPageGroup';

const PaginationNextPageGroup = ({
  className,
  isDisabled,
  ...props
}: React.ComponentProps<typeof PaginationLink>) => (
  <PaginationLink
    size="default"
    className={cn(className)}
    isDisabled={isDisabled}
    {...props}
  >
    <ChevronSvg
      stroke={isDisabled ? '#DADEE5' : '#6D717D'}
      className={'rotate-180'}
      width={24}
      height={24}
    />
  </PaginationLink>
);
PaginationNextPageGroup.displayName = 'PaginationNextPageGroup';

const PaginationJumpFirst = ({
  className,
  isDisabled,
  ...props
}: React.ComponentProps<typeof PaginationLink>) => (
  <PaginationLink
    size="default"
    className={cn(className)}
    isDisabled={isDisabled}
    {...props}
  >
    <ChevronDoubleSvg
      stroke={isDisabled ? '#DADEE5' : '#6D717D'}
      width={24}
      height={24}
    />
  </PaginationLink>
);
PaginationJumpFirst.displayName = 'PaginationJumpFirst';

const PaginationJumpLast = ({
  className,
  isDisabled,
  ...props
}: React.ComponentProps<typeof PaginationLink>) => (
  <PaginationLink
    size="default"
    className={cn(className)}
    isDisabled={isDisabled}
    {...props}
  >
    <ChevronDoubleSvg
      stroke={isDisabled ? '#DADEE5' : '#6D717D'}
      width={24}
      height={24}
      className={'rotate-180'}
    />
  </PaginationLink>
);
PaginationJumpLast.displayName = 'PaginationJumpLast';

const PaginationEllipsis = ({
  className,
  ...props
}: React.ComponentProps<'span'>) => (
  <span
    aria-hidden
    className={cn('body-16r text-[#6D717D]', className)}
    {...props}
  >
    {'···'}
  </span>
);
PaginationEllipsis.displayName = 'PaginationEllipsis';

export {
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationJumpFirst,
  PaginationJumpLast,
  PaginationLink,
  PaginationNextPageGroup,
  PaginationPreviousPageGroup,
  PaginationRoot
};
